import { encode, decode, isBase62 } from "./base62";

const MAX_64_BIT_INT_VALUE = (1n << 64n) - 1n

const is64BitInt = (value: bigint) => value >= 0n && value <= MAX_64_BIT_INT_VALUE;

export class Id {
  private static lastId?: bigint = undefined;

  private constructor(private readonly value: bigint) {
    if (value === undefined) throw new Error("value must be defined");
    if (value === null     ) throw new Error("value must be defined");
    if (value < 1n         ) throw new Error("value must be positive");
    if (!is64BitInt(value) ) throw new Error("value must be a valid 64-bit integer");
  }

  get asInt(): bigint { return this.value;          }
  get asStr(): string { return encode(this.value);  }

  toString(): string {
    return this.asStr;
  }
  toJSON(): any {
    return this.asStr;
  }

  // Create an Id from an 64-bit integer or a base62 string
  static from<TId extends Id>(value: bigint | string): TId {
    if (value === undefined) throw new Error("value must be defined");
    if (value === null     ) throw new Error("value must be defined");

    if (typeof value === "bigint") {
      if (value < 1n        ) throw new Error("value must be positive");
      if (!is64BitInt(value)) throw new Error("value must be a valid 64-bit integer");

      return new Id(value) as TId;
    }

    if (typeof value === "string") {
      if (value.length === 0) throw new Error("value must not be empty");
      if (!isBase62(value)  ) throw new Error("value must be a valid base62 string");

      const decoded = decode(value);
      if (decoded < 1n) throw new Error("value must be positive 64-bit integer encoded as a base62 string");
      if (!is64BitInt(decoded)) throw new Error("value must be a valid 64-bit integer encoded as a base62 string");

      return new Id(decoded) as TId;
    }

    throw new Error("value must be a bigint or a string");
  }
  static is(value: any): value is Id {
    return value instanceof Id;
  }
  static areEqual<TId extends Id>(a: TId, b: TId): boolean {
    return a.asInt === b.asInt;
  }
}
